import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import axiosInstance, { ensureCsrfCookie } from "src/common/axiosInstance";
import { clearRequestCache } from "src/common/requestCache";
import { storeConnection } from "src/common/whatsappConnection";
import {
  WHATSAPP_SETUP,
  PHONE_NUMBER_ID,
  CURRENT_WORKSPACE_KEY,
} from "src/config/constants";
import {
  beginDeveloperLogin,
  clearDeveloperTokens,
  isDeveloperOAuthEnabled,
} from "src/services/developerOAuth";

export const hydrateWhatsappSetup = async (isCurrent = () => true) => {
  try {
    const res = await axiosInstance.get("/me/settings");
    if (!isCurrent()) return;
    storeConnection(res.data?.data);
  } catch {
    if (!isCurrent()) return;
    // An unverified cached connection can belong to a previous account.
    storeConnection(null);
  }
};

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const loginInFlight = useRef(null);
  const refreshInFlight = useRef(null);
  const refreshVersion = useRef(0);
  const [auth, setAuth] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const refreshSession = useCallback(() => {
    if (loginInFlight.current) return Promise.resolve(null);
    if (refreshInFlight.current) return refreshInFlight.current;
    refreshInFlight.current = (async () => {
      const version = ++refreshVersion.current;
      const isCurrent = () => version === refreshVersion.current;
      try {
        await ensureCsrfCookie();
        const response = await axiosInstance.get("/api/auth/me");
        if (!isCurrent()) return null;
        clearRequestCache();
        await hydrateWhatsappSetup(isCurrent);
        if (!isCurrent()) return null;
        setCurrentUser(response.data.data);
        setAuth(true);
        setAuthError(null);
        return response.data.data;
      } catch (error) {
        if (!isCurrent()) return null;
        setCurrentUser(null);
        setAuth(false);
        setAuthError(error.response?.status === 403 ? "access_denied" : null);
        return null;
      } finally {
        if (isCurrent()) setLoading(false);
      }
    })().finally(() => {
      refreshInFlight.current = null;
    });
    return refreshInFlight.current;
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const revalidateWhenActive = () => {
      if (document.visibilityState === "visible") refreshSession();
    };

    window.addEventListener("focus", revalidateWhenActive);
    document.addEventListener("visibilitychange", revalidateWhenActive);

    return () => {
      window.removeEventListener("focus", revalidateWhenActive);
      document.removeEventListener("visibilitychange", revalidateWhenActive);
    };
  }, [refreshSession]);

  const beginLogin = useCallback(
    (returnTo = `${window.location.pathname}${window.location.search}`) => {
      if (!loginInFlight.current) {
        loginInFlight.current = Promise.resolve()
          .then(() => {
            if (isDeveloperOAuthEnabled()) return beginDeveloperLogin(returnTo);
            const params = new URLSearchParams({ return_to: returnTo || "/" });
            const workspace = new URLSearchParams(window.location.search).get(
              "workspace",
            );
            if (workspace) params.set("workspace", workspace);
            window.location.assign(
              `${process.env.REACT_APP_BASE_URL}/auth/autoappy/redirect?${params.toString()}`,
            );
          })
          .catch((error) => {
            loginInFlight.current = null;
            throw error;
          });
      }
      return loginInFlight.current;
    },
    [],
  );

  const clearLocalState = useCallback(() => {
    refreshVersion.current += 1;
    [
      WHATSAPP_SETUP,
      PHONE_NUMBER_ID,
      CURRENT_WORKSPACE_KEY,
      "token",
      "userData",
      "lastFetched",
    ].forEach((key) => localStorage.removeItem(key));
    clearRequestCache();
    clearDeveloperTokens();
    setCurrentUser(null);
    setAuth(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await ensureCsrfCookie();
      await axiosInstance.post("/auth/logout");
    } finally {
      clearLocalState();
    }
  }, [clearLocalState]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        currentUser,
        setCurrentUser,
        loading,
        authError,
        isEmailVerified: Boolean(currentUser?.email_verified_at),
        isAdmin: false,
        refreshSession,
        beginLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
